import { useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IMPORT_COMPLETE_ROOTS, IMPORT_TIMEOUT_MESSAGE, IMPORT_TTL_MS, isImportFinished } from '@/api/mutations/useImportFile';
import { listenSse } from '@/api/sse/listenSse';
import { tokenAtom } from '@/atoms/auth';
import { activeImportMarkerAtom, importInProgressAtom } from '@/atoms/import';
import { jotaiStore } from '@/atoms/store';
import { snackbarColor } from '@/enums';
import { useSnackbar } from '@/hooks';
import type { SnackbarType, SseJobEvent } from '@/types';

// The longest a healthy import stays silent (keep-alives only) is one DeepSeek
// call — the DeepSeekService Guzzle timeout is 1000s (~17 min).
const DEEPSEEK_TIMEOUT_MS = 1000 * 1000;
// Slack on top of the silence window for queue latency and event delivery.
const IDLE_GRACE_MS = 90 * 1000;

// Maps import SSE events to snackbars. Shared by the live import flow
// (EditorHeader) and the resume-after-refresh listener below.
export function createImportSseHandlers({ showSnackbar, t }: { showSnackbar: (payload: SnackbarType) => void; t: TFunction }) {
    // Don't overwrite a reported error with the "✓" snackbar, and don't show "✓"
    // on a silent resume (the import had already finished before we attached).
    let hadError = false;
    let receivedAny = false;

    const show = (event: SseJobEvent) => {
        if (event.type === 'progress') {
            showSnackbar({ open: true, message: event.message, severity: snackbarColor.success, progress: event.percent });
            return;
        }
        if (event.status === 'error') hadError = true;
        showSnackbar({
            open: true,
            message: event.message,
            severity: event.status === 'error' ? snackbarColor.error : snackbarColor.success,
            progress: undefined,
        });
    };

    let primed = false;

    return {
        // Re-display a persisted event after a refresh. Doesn't count as live
        // traffic: `receivedAny` must reflect only what arrives on this
        // connection, since it gates the "✓" snackbar on silent completion.
        primeLastKnown: (event: SseJobEvent) => {
            primed = true;
            show(event);
        },
        onSseEvent: (event: SseJobEvent) => {
            receivedAny = true;
            // Keep the persisted marker fresh so the next refresh can show
            // where the import stood and measure silence from the right point.
            const marker = jotaiStore.get(activeImportMarkerAtom);
            if (marker) {
                jotaiStore.set(activeImportMarkerAtom, { ...marker, last_event: event, last_event_at: Date.now() });
            }
            show(event);
        },
        onSseError: (error: string) => {
            hadError = true;
            showSnackbar({ open: true, message: error, severity: snackbarColor.error, progress: undefined });
        },
        onSseComplete: () => {
            if (!receivedAny) {
                // Silent end (import had already finished before we attached).
                // A primed progress snackbar never auto-hides — dismiss it.
                if (primed) showSnackbar({ open: false, message: '', progress: undefined });
                return;
            }
            if (hadError) return;
            showSnackbar({ open: true, message: `${t('import_doc')} ✓`, severity: snackbarColor.success, progress: undefined });
        },
    };
}

// Module-level so React StrictMode remounts and Layout remounts can't start a
// second listener; the listener itself lives for the rest of the session.
let resumeAttempted = false;

/**
 * Re-attaches to the import SSE stream after a page refresh. The backend keeps
 * processing the import regardless of the client, so if a persisted marker says
 * an import was in flight, we re-lock the project and stream progress again
 * until the terminal event, an idle timeout, or the marker's TTL.
 */
export function useResumeImportSse() {
    const { t } = useTranslation();
    const showSnackbar = useSnackbar();
    const queryClient = useQueryClient();

    useEffect(() => {
        if (resumeAttempted) return;
        resumeAttempted = true;

        // Refetch everything the finished import rewrote (editor content + the
        // import-reports list), mirroring the live mutation's `invalidate`.
        const invalidateAfterImport = () => {
            for (const root of IMPORT_COMPLETE_ROOTS) {
                queryClient.invalidateQueries({ queryKey: root });
            }
        };

        const marker = jotaiStore.get(activeImportMarkerAtom);
        if (!marker) return;

        // The live mutation owns the lock in this tab; nothing to resume.
        if (jotaiStore.get(importInProgressAtom)) return;

        const deadlineAt = marker.started_at + IMPORT_TTL_MS;
        if (Date.now() > deadlineAt) {
            jotaiStore.set(activeImportMarkerAtom, null);
            return;
        }

        // The refresh can race the marker cleanup: if the last persisted
        // message was already the terminal one, the import is over — recap how
        // it ended and never re-lock.
        if (marker.last_event && isImportFinished(marker.last_event)) {
            jotaiStore.set(activeImportMarkerAtom, null);
            invalidateAfterImport();
            showSnackbar({ open: true, message: `${t('import_doc')} ✓`, severity: snackbarColor.success, progress: undefined });
            return;
        }
        if (marker.last_event?.type === 'event' && marker.last_event.status === 'error') {
            jotaiStore.set(activeImportMarkerAtom, null);
            showSnackbar({ open: true, message: marker.last_event.message, severity: snackbarColor.error, progress: undefined });
            return;
        }

        jotaiStore.set(importInProgressAtom, true);

        const { primeLastKnown, ...handlers } = createImportSseHandlers({ showSnackbar, t });

        // Show where the import stood before the refresh (the backend can't
        // replay missed events) while we wait for live ones.
        if (marker.last_event) {
            primeLastKnown(marker.last_event);
        }

        // Silence is measured from the last persisted event, so time that
        // already passed while the page was closed counts toward the wait:
        // reopening long after the import went quiet unlocks within the grace
        // period instead of waiting out the full DeepSeek window again.
        const silentSince = marker.last_event_at ?? marker.started_at;
        const initialIdleTimeoutMs = Math.max(0, silentSince + DEEPSEEK_TIMEOUT_MS - Date.now()) + IDLE_GRACE_MS;

        listenSse({
            token: jotaiStore.get(tokenAtom),
            projectId: marker.project_id,
            signal: new AbortController().signal,
            deadlineAt,
            isFinished: isImportFinished,
            timeoutMessage: IMPORT_TIMEOUT_MESSAGE,
            idleTimeoutMs: DEEPSEEK_TIMEOUT_MS + IDLE_GRACE_MS,
            initialIdleTimeoutMs,
            ...handlers,
        }).finally(() => {
            jotaiStore.set(importInProgressAtom, false);
            jotaiStore.set(activeImportMarkerAtom, null);
            invalidateAfterImport();
        });
    }, [showSnackbar, t, queryClient]);
}
